import { useCallback, useMemo, useSyncExternalStore } from "react";

const listenersByKey = new Map<string, Set<() => void>>();

const notifyListeners = (key: string) => {
  for (const listener of listenersByKey.get(key) ?? []) listener();
};

const handleStorageEvent = (event: StorageEvent) => {
  if (event.key !== null) {
    notifyListeners(event.key);
    return;
  }

  for (const key of listenersByKey.keys()) notifyListeners(key);
};

const subscribeToKey = (key: string, listener: () => void) => {
  if (listenersByKey.size === 0) {
    window.addEventListener("storage", handleStorageEvent);
  }

  const listeners = listenersByKey.get(key) ?? new Set();
  listeners.add(listener);
  listenersByKey.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) listenersByKey.delete(key);
    if (listenersByKey.size === 0) {
      window.removeEventListener("storage", handleStorageEvent);
    }
  };
};

const readJson = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`Error reading localStorage key "${key}":`, error);
    return null;
  }
};

const writeJson = (key: string, json: string | null) => {
  try {
    if (window.localStorage.getItem(key) === json) return;

    if (json === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, json);
    }
  } catch (error) {
    console.warn(`Error writing localStorage key "${key}":`, error);
    return;
  }

  notifyListeners(key);
};

const parseJson = <T>(
  key: string,
  json: string | null,
): { value: T } | null => {
  if (!json || json === "undefined") return null;

  try {
    return { value: JSON.parse(json) };
  } catch (error) {
    console.warn(`Error parsing localStorage key "${key}":`, error);
    return null;
  }
};

const getServerJson = () => null;

export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const subscribe = useCallback(
    (listener: () => void) => subscribeToKey(key, listener),
    [key],
  );
  const getJson = useCallback(() => readJson(key), [key]);

  const json = useSyncExternalStore(subscribe, getJson, getServerJson);
  const stored = useMemo(() => parseJson<T>(key, json), [key, json]);

  const setValue = useCallback(
    (value: T) =>
      writeJson(key, value === undefined ? null : JSON.stringify(value)),
    [key],
  );
  const removeValue = useCallback(() => writeJson(key, null), [key]);

  return [stored ? stored.value : initialValue, setValue, removeValue] as const;
};
